import { message } from 'antd';

const extractDetailObject = (detail) => {
  if (!detail || Array.isArray(detail)) {
    return undefined;
  }
  if (typeof detail === 'object') {
    return detail;
  }
  return undefined;
};

const isNetworkOrTimeoutError = (error) => {
  if (!error) return false;

  if (!error.response && (error.request || error.code === 'ECONNABORTED')) {
    return true;
  }

  return ['ERR_NETWORK', 'ECONNABORTED', 'ETIMEDOUT'].includes(error.code);
};

const getStatusMessage = (status, context) => {
  const isLogin = context === 'login';
  const isSession = context === 'session';
  const isResetPassword = context === 'reset-password';

  if (status === 400) {
    return 'Não foi possível concluir a solicitação. Confira os dados e tente novamente.';
  }

  if (status === 401) {
    if (isLogin) return 'Usuário ou senha inválidos.';
    if (isResetPassword) return 'Link inválido ou expirado.';
    if (isSession) return 'Sua sessão expirou. Entre novamente.';
    return 'Sua sessão expirou. Entre novamente.';
  }

  if (status === 403) {
    return 'Você não tem permissão para realizar esta ação.';
  }

  if (status === 404) {
    return 'Informação não encontrada.';
  }

  if (status === 409) {
    return 'Não foi possível concluir a operação por conflito de dados.';
  }

  if (status === 422) {
    return 'Os dados informados são inválidos. Revise e tente novamente.';
  }

  if (status === 429) {
    return 'Muitas tentativas em sequência. Aguarde um instante e tente novamente.';
  }

  if ([500, 502, 503, 504].includes(status)) {
    return 'Não foi possível carregar os dados. Tente novamente em instantes.';
  }

  return undefined;
};

const getSaasEntitlementFriendlyMessage = (detailObject) => {
  const code = detailObject?.code;
  if (code === 'SAAS_LIMIT_EXCEEDED') {
    const featureCode = String(detailObject?.feature_code || '').toUpperCase();
    if (featureCode === 'MAX_ALUNOS') {
      return 'Limite de alunos atingido. Para continuar, solicite a alteração do plano.';
    }
    if (featureCode === 'MAX_PROFESSORES') {
      return 'Limite de membros da equipe atingido. Para continuar, solicite a alteração do plano.';
    }
    return 'Limite do plano atingido. Para continuar, solicite a alteração do plano.';
  }
  if (code === 'SAAS_ENTITLEMENT_BLOCKED') {
    return 'Este recurso não está disponível no plano atual. Para utilizar, solicite a alteração do plano.';
  }
  return undefined;
};

export const parseApiError = (error) => {
  const status = error?.response?.status;
  const data = error?.response?.data || {};
  const detail = data?.detail;
  const detailObject = extractDetailObject(detail);
  const detailString = typeof detail === 'string' ? detail : undefined;

  // Prioridade: detail.user_message -> detail string -> data.user_message
  const saasFriendlyMessage = getSaasEntitlementFriendlyMessage(detailObject);

  const userMessage =
    saasFriendlyMessage ||
    detailObject?.user_message ||
    detailString ||
    (typeof data?.user_message === 'string' ? data.user_message : undefined);

  const missingFields = Array.isArray(detailObject?.missing_fields)
    ? detailObject.missing_fields
    : Array.isArray(data?.missing_fields)
      ? data.missing_fields
      : undefined;

  return {
    status,
    userMessage,
    missingFields,
  };
};

export const buildApiErrorMessage = (error, fallbackMessage, options = {}) => {
  const { status, userMessage, missingFields } = parseApiError(error);
  const context = options?.context;
  let finalMessage = userMessage || fallbackMessage;

  if (isNetworkOrTimeoutError(error)) {
    finalMessage = 'Não foi possível conectar ao servidor. Verifique sua internet e tente novamente.';
  }

  const statusMessage = getStatusMessage(status, context);
  if (!finalMessage && statusMessage) {
    finalMessage = statusMessage;
  }

  if (status === 502 && !userMessage) {
    finalMessage = 'Falha na integração com o parceiro financeiro. Tente novamente em instantes.';
  }

  if (missingFields?.length) {
    const campos = missingFields
      .map((field) => String(field).replace(/_/g, ' '))
      .join(', ');
    const complemento = `Campos faltantes: ${campos}.`;
    finalMessage = finalMessage ? `${finalMessage} ${complemento}` : complemento;
  }

  return {
    status,
    message: finalMessage || 'Algo deu errado. Tente novamente.',
    missingFields,
  };
};

export const handleApiError = (error, fallbackMessage) => {
  const { message: finalMessage } = buildApiErrorMessage(error, fallbackMessage);
  message.error(finalMessage);
};
